/* aw-zip-gate.js
 * ZIP delivery-eligibility gate: served (free) / nearby (surcharge) / beyond (waitlist).
 * Data lazy-loads on first focus so it never blocks LCP. Zero network on input.
 * Persists { state, zip, fee } to localStorage + cart attributes; the order widget
 * and checkout enforcement read the same values.
 */
const root = document.getElementById('aw-zip-gate');
if (root) init(root);

function cfg(el) {
  return {
    servedUrl: el.dataset.servedUrl,
    surchargeUrl: el.dataset.surchargeUrl,
    regionsUrl: el.dataset.regionsUrl,
    notesUrl: el.dataset.notesUrl,
    mode: el.dataset.feeMode,
    flatFee: Number(el.dataset.flatFee),
    per5: Number(el.dataset.per5),
    maxMiles: Number(el.dataset.maxMiles),
    feeCap: Number(el.dataset.feeCap) || 0,
    klaviyoKey: el.dataset.klaviyoKey,
    klaviyoList: el.dataset.klaviyoList,
  };
}

let DATA = null;
async function loadData(c) {
  if (DATA) return DATA;
  const [served, surcharge, regions, notes] = await Promise.all([
    fetch(c.servedUrl).then((r) => r.json()),
    fetch(c.surchargeUrl).then((r) => r.json()),
    c.regionsUrl
      ? fetch(c.regionsUrl).then((r) => r.json()).catch(() => null)
      : Promise.resolve(null),
    c.notesUrl
      ? fetch(c.notesUrl).then((r) => r.json()).catch(() => null)
      : Promise.resolve(null),
  ]);
  DATA = { served: new Set(served.map(String)), surcharge, regions, notes };
  return DATA;
}

/* Route-zone cadence for a ZIP ({ label, weeks }), or null for the default zones. */
function regionFor(zip, data) {
  const r = data.regions;
  const key = r && r.zips && r.zips[zip];
  return (key && r.regions[key]) || null;
}

/* Coming-soon / informational note for a served ZIP ({ note, cta }), or null.
 * Oregon (2026-07-19, Brian): the 16 Portland-area ZIPs are orderable now but
 * delivery starts Q4 2026, so a served result also shows the note. */
function noteFor(zip, data) {
  const groups = data.notes;
  if (!Array.isArray(groups)) return null;
  for (const g of groups) {
    if (g && g.zips && g.zips.indexOf(zip) > -1) return { note: g.note || '', cta: g.cta || '' };
  }
  return null;
}

function feeForMiles(miles, c) {
  if (miles == null || miles > c.maxMiles) return null;
  if (c.mode === 'flat') return c.flatFee;
  let fee = Math.ceil(miles / 5) * c.per5;
  if (c.feeCap) fee = Math.min(fee, c.feeCap);
  return fee;
}

function resolve(zip, data, c) {
  const region = regionFor(zip, data);
  const cadence = region ? { label: region.label, weeks: region.weeks } : null;
  const note = noteFor(zip, data);
  if (data.served.has(zip)) return { state: 'served', zip, fee: 0, miles: 0, cadence, note };
  const miles = data.surcharge[zip];
  const fee = feeForMiles(miles, c);
  // A ZIP that resolves to a $0 fee (shares a centroid with a served ZIP) is served, not "surcharge $0".
  if (fee === 0) return { state: 'served', zip, fee: 0, miles, cadence, note };
  if (fee != null) return { state: 'surcharge', zip, fee, miles, cadence, note };
  return { state: 'waitlist', zip, fee: null, miles: miles == null ? null : miles, cadence: null };
}

function cadenceSentence(cadence) {
  if (!cadence) return '';
  return ' We deliver to ' + cadence.label + ' once every ' + cadence.weeks + ' weeks.';
}

async function persist(res) {
  try { localStorage.setItem('aw_delivery', JSON.stringify(res)); } catch (_) {}
  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          'Delivery ZIP': res.zip,
          'Delivery Eligibility': res.state,
          'Delivery Fee': res.fee == null ? '' : String(res.fee),
          'Delivery Cadence': res.cadence ? 'once every ' + res.cadence.weeks + ' weeks (' + res.cadence.label + ')' : '',
          // Flags Oregon pre-orders so ops can hold them for the Q4 2026 launch.
          'Delivery Note': res.note && res.note.note ? res.note.note : '',
        },
      }),
    });
  } catch (_) { /* non-blocking */ }
}

/* Same customer-facing money format as aw-money.liquid / usd() in the other
 * aw-* modules (fees are whole dollars, so decimals only ever appear if the
 * fee config changes to a fractional value). */
function money(n) {
  const v = Number(n);
  const digits = v % 1 === 0 ? 0 : 2;
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

function render(res, els) {
  const { result, waitlist, changeBtn } = els;
  waitlist.hidden = true;
  result.hidden = false;
  changeBtn.hidden = false;
  if (res.state === 'served') {
    result.className = 'aw-gate__result is-yes';
    if (res.note && res.note.note) {
      // Orderable now, but the note sets delivery expectations (Oregon Q4 2026).
      result.textContent = res.note.note + (res.note.cta ? ' ' + res.note.cta : '');
    } else {
      result.textContent = res.cadence
        ? 'Free delivery to ' + res.cadence.label + ', once every ' + res.cadence.weeks + ' weeks.'
        : 'We deliver to you, and delivery in your area is free.';
    }
  } else if (res.state === 'surcharge') {
    result.className = 'aw-gate__result is-fee';
    result.textContent = 'Good news, we deliver to you. You are just outside our core area, so a ' + money(res.fee) + ' delivery fee applies to each delivery.' + cadenceSentence(res.cadence);
  } else {
    result.className = 'aw-gate__result is-no';
    result.textContent = 'We are not in ' + res.zip + ' yet. Add your email and we will tell you the moment we are.';
    waitlist.hidden = false;
  }
  document.dispatchEvent(new CustomEvent('aw:eligibility', { detail: res }));
}

async function joinWaitlist(res, c, emailInput) {
  const email = (emailInput.value || '').trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { emailInput.focus(); return false; }
  if (!c.klaviyoKey || !c.klaviyoList) return false;
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: { waitlist_zip: res.zip, source: 'zip-gate waitlist' },
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: c.klaviyoList } } },
    },
  };
  try {
    const r = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + c.klaviyoKey, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body),
    });
    return r.ok || r.status === 202;
  } catch (_) { return false; }
}

function init(el) {
  const c = cfg(el);
  const form = document.getElementById('aw-zip-form');
  const input = document.getElementById('aw-zip-input');
  const result = document.getElementById('aw-zip-result');
  const waitlist = document.getElementById('aw-zip-waitlist');
  const emailInput = document.getElementById('aw-zip-email');
  const joinBtn = document.getElementById('aw-zip-join');
  const changeBtn = document.getElementById('aw-zip-change');
  const els = { result, waitlist, changeBtn };

  input.addEventListener('focus', () => loadData(c), { once: true });

  // Returning visitor: show their saved state, offer a change link, never re-ask.
  try {
    const cached = localStorage.getItem('aw_delivery');
    if (cached) {
      const res = JSON.parse(cached);
      if (res && res.zip) { input.value = res.zip; render(res, els); }
    }
  } catch (_) {}

  changeBtn.addEventListener('click', () => {
    input.value = '';
    result.hidden = true;
    waitlist.hidden = true;
    changeBtn.hidden = true;
    try { localStorage.removeItem('aw_delivery'); } catch (_) {}
    document.dispatchEvent(new CustomEvent('aw:eligibility', { detail: null }));
    input.focus();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const zip = (input.value || '').replace(/\D/g, '').slice(0, 5);
    if (zip.length !== 5) {
      result.hidden = false;
      result.className = 'aw-gate__result is-no';
      result.textContent = 'Please double-check your ZIP, it should be 5 digits.';
      return;
    }
    const data = await loadData(c);
    const res = resolve(zip, data, c);
    render(res, els);
    if (res.state !== 'waitlist') persist(res);
  });

  joinBtn.addEventListener('click', async () => {
    const zip = (input.value || '').replace(/\D/g, '').slice(0, 5);
    const res = resolve(zip, await loadData(c), c);
    joinBtn.disabled = true;
    const ok = await joinWaitlist(res, c, emailInput);
    joinBtn.disabled = false;
    result.hidden = false;
    result.className = 'aw-gate__result ' + (ok ? 'is-yes' : 'is-no');
    result.textContent = ok
      ? 'You are on the list. We will email you the moment we deliver to ' + res.zip + '.'
      : 'Something went wrong, please try again.';
  });
}
